import React, { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Textarea,
} from '@nextui-org/react';
import { Flag } from 'lucide-react';
import forumService, {
  ForumReportReason,
  ForumVoteTargetType,
  extractForumError,
} from '../../services/forumService';

const TEAL = '#3FCED2';

/** Meldegründe – Port aus forum_report_modal.dart der Flutter-App. */
const REASONS: { value: ForumReportReason; label: string }[] = [
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Belästigung' },
  { value: 'hate_speech', label: 'Hassrede' },
  { value: 'misinformation', label: 'Falschinformation' },
  { value: 'violence', label: 'Gewalt' },
  { value: 'inappropriate', label: 'Unangemessen' },
  { value: 'copyright', label: 'Urheberrecht' },
  { value: 'other', label: 'Sonstiges' },
];

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetType: ForumVoteTargetType;
  targetId: string;
  onReported?: () => void;
}

/**
 * Melden-Modal (Flag) für Topics und Posts.
 * Backend verlangt reason (Enum) UND description (mind. 10 Zeichen).
 */
const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  targetType,
  targetId,
  onReported,
}) => {
  const [reason, setReason] = useState<ForumReportReason>('spam');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setReason('spam');
    setDescription('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    const desc = description.trim();
    if (desc.length < 10) {
      setError('Bitte beschreibe das Problem genauer (mind. 10 Zeichen).');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await forumService.report({
        targetType,
        targetId,
        reason,
        description: desc,
      });
      handleClose();
      onReported?.();
    } catch (err) {
      setError(extractForumError(err, 'Meldung konnte nicht gesendet werden'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-zinc-900 border border-zinc-800 text-white',
        closeButton: 'text-gray-400 hover:bg-zinc-800',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          <Flag size={18} className="text-red-500" />
          {targetType === 'topic' ? 'Topic melden' : 'Beitrag melden'}
        </ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-400 mb-1">
            Warum möchtest du diesen Inhalt melden?
          </p>
          <div className="flex flex-wrap gap-2">
            {REASONS.map((r) => {
              const selected = reason === r.value;
              return (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setReason(r.value)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    selected
                      ? 'border-transparent font-semibold'
                      : 'border-zinc-700 text-gray-300 hover:bg-zinc-800'
                  }`}
                  style={
                    selected
                      ? { backgroundColor: TEAL, color: '#000' }
                      : undefined
                  }
                >
                  {r.label}
                </button>
              );
            })}
          </div>

          <Textarea
            value={description}
            onValueChange={setDescription}
            label="Beschreibung"
            placeholder="Beschreibe das Problem (mind. 10 Zeichen)..."
            minRows={3}
            maxLength={1000}
            classNames={{
              inputWrapper: 'bg-zinc-800 data-[hover=true]:bg-zinc-800',
              input: 'text-white',
              label: 'text-gray-400',
            }}
            className="mt-3"
          />

          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="flat"
            className="bg-zinc-800 text-gray-300"
            onPress={handleClose}
            isDisabled={submitting}
          >
            Abbrechen
          </Button>
          <Button
            color="danger"
            onPress={handleSubmit}
            isLoading={submitting}
          >
            Melden
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ReportModal;
